import {
  GreenCardData,
  InsuranceTerm,
  PossessionBaseEnum
} from '@insurance/api/requests'
import { InputDatePicker, RadioCardGroup } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Text } from '@shared/components'
import { Col, Divider, Form, Input, Row, Select, Spin } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { useGreenCardInsuranceValues } from './GreenCardInsuranceContext'
import { formProps, zoneLabelMap } from './utils'
import { CreditFields } from '../CreditFields'
import { CustomerFields } from '../PersonsFields'

export const InformationStep = () => {
  const { t } = useTranslation()
  const { required } = useValidationsField()

  const {
    validFromProps,
    possessionBaseProps,
    possessionBaseDocNumberProps,
    possessionBaseDocDateProps,
    possessionBaseDocNoteProps,
    vehicleDataProps,
    zoneProps,
    termProps,
    priceKey,
    isPropertyKey
  } = formProps(t)

  const { handleRecalculate, isCalculatingLoading } =
    useGreenCardInsuranceValues()

  const price = Form.useWatch(priceKey.name, { preserve: true })

  const possessionBase = Form.useWatch<PossessionBaseEnum>(
    possessionBaseProps.name,
    { preserve: true }
  )

  const showPossessionBaseDocFields =
    !!possessionBase && possessionBase !== PossessionBaseEnum.PROPERTY

  const vehicleData = Form.useWatch<GreenCardData>(vehicleDataProps.name, {
    preserve: true
  })

  const isProperty = Form.useWatch<boolean>(isPropertyKey.name, {
    preserve: true
  })

  const possessionBaseOptions = Object.values(PossessionBaseEnum).map(
    (value) => ({ label: t(value), value })
  )

  const termOptions = Object.values(InsuranceTerm).map((value) => ({
    label: t(value),
    value
  }))

  const zoneOptions = Object.entries(zoneLabelMap).map(([value, label]) => ({
    label: t(label),
    value
  }))

  const contractorName = vehicleData
    ? [vehicleData.first_name, vehicleData.last_name].filter(Boolean).join(' ')
    : undefined

  const vehicleInfo = vehicleData
    ? [vehicleData.brand, vehicleData.model, vehicleData.registration_number]
        .filter(Boolean)
        .join(' ')
    : undefined

  const trailerInfo = vehicleData?._trailer
    ? [
        vehicleData._trailer.brand,
        vehicleData._trailer.model,
        vehicleData._trailer.registration_number
      ]
        .filter(Boolean)
        .join(' ')
    : undefined

  return (
    <Spin spinning={isCalculatingLoading}>
      {vehicleData && (
        <>
          <Row gutter={[12, 12]}>
            {contractorName && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('contractor')} className="m-0">
                  <Text strong color="colorPrimary">
                    {contractorName}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {vehicleInfo && (
              <Col xs={24} lg={12}>
                <Form.Item
                  label={
                    trailerInfo
                      ? t('towing_vehicle_brand_and_model')
                      : t('vehicle_brand_and_model')
                  }
                  className="m-0"
                >
                  <Text strong color="colorPrimary">
                    {vehicleInfo}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {trailerInfo && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('trailer_brand_and_model')} className="m-0">
                  <Text strong color="colorPrimary">
                    {trailerInfo}
                  </Text>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider />
        </>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Form.Item {...validFromProps} rules={[required]}>
            <InputDatePicker
              placeholder={validFromProps.label}
              minDate={dayjs()}
              maxDate={dayjs().add(6, 'months')}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item {...termProps} rules={[required]}>
            <Select
              placeholder={termProps.label}
              options={termOptions}
              onChange={handleRecalculate}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item {...zoneProps} rules={[required]}>
            <RadioCardGroup
              options={zoneOptions}
              onChange={handleRecalculate}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            {...possessionBaseProps}
            rules={isProperty ? [] : [required]}
          >
            <Select
              placeholder={possessionBaseProps.label}
              options={possessionBaseOptions}
              allowClear
              onChange={handleRecalculate}
            />
          </Form.Item>
        </Col>

        {showPossessionBaseDocFields && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item {...possessionBaseDocNumberProps}>
                <Input
                  placeholder={possessionBaseDocNumberProps.label}
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item {...possessionBaseDocDateProps}>
                <InputDatePicker
                  placeholder={possessionBaseDocDateProps.label}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item {...possessionBaseDocNoteProps}>
                <Input.TextArea
                  placeholder={possessionBaseDocNoteProps.label}
                  maxLength={300}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Divider />

      <CustomerFields isEditable />
      <CreditFields orderPrice={price?.amount} />
    </Spin>
  )
}
